// src/hooks/useAuditoria.js
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const FILTROS_INICIALES = {
    busqueda: '',
    accion: '',
    usuario: '',
    recurso: '',
    recursoId: '',
    desde: '',
    hasta: ''
};

const LIMITE = 25;

export function useAuditoria() {
    const { showToast } = useToast();

    const [logs, setLogs] = useState([]);
    const [profesionales, setProfesionales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagina, setPagina] = useState(1);
    const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

    // Un único estado de filtros que dispara la consulta de forma reactiva.
    const [filtros, setFiltros] = useState(FILTROS_INICIALES);

    // Carga de la plantilla para el selector "por profesional"
    useEffect(() => {
        api.get('/profesionales')
            .then(res => setProfesionales(Array.isArray(res) ? res : (res?.data || [])))
            .catch(() => { /* no bloqueante */ });
    }, []);

    const cargarLogs = useCallback(async (filtrosActivos, paginaActiva) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('pagina', paginaActiva);
            params.set('limite', LIMITE);
            Object.entries(filtrosActivos).forEach(([k, v]) => {
                if (v && String(v).trim()) params.set(k, v);
            });

            const res = await api.get(`/auditoria?${params.toString()}`);
            const lista = Array.isArray(res) ? res : (res?.data || []);
            setLogs(lista);

            const pag = res?.pagination || {};
            setMeta({
                total: pag.totalDocs ?? lista.length,
                totalPages: pag.totalPages ?? 1
            });
        } catch (err) {
            showToast('No se pudo cargar el registro de auditoría.', 'error');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // 🔎 Filtrado REACTIVO con debounce: cualquier cambio en filtros o página
    // relanza la búsqueda ~300 ms después (sin botón "Filtrar"), para una
    // búsqueda ágil mientras se teclea o se cambian los desplegables.
    const primeraCarga = useRef(true);
    useEffect(() => {
        const ms = primeraCarga.current ? 0 : 300;
        primeraCarga.current = false;
        const t = setTimeout(() => cargarLogs(filtros, pagina), ms);
        return () => clearTimeout(t);
    }, [filtros, pagina, cargarLogs]);

    // Cambiar un filtro vuelve siempre a la página 1
    const setFiltro = (campo, valor) => {
        setPagina(1);
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    };

    const limpiarFiltros = () => {
        setPagina(1);
        setFiltros(FILTROS_INICIALES);
    };

    const hayFiltrosActivos = Object.values(filtros).some(v => v && String(v).trim());

    return {
        logs, loading, profesionales,
        pagina, setPagina, meta,
        filtros, setFiltro,
        limpiarFiltros, hayFiltrosActivos,
        recargar: () => cargarLogs(filtros, pagina)
    };
}
